import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { API } from "../App";
import axios from "axios";
import { TrendingUp, TrendingDown, AlertTriangle, Wallet, ShoppingCart, ArrowUpRight, Clock } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const Dashboard = () => {
  const [ozet, setOzet] = useState(null);
  const [aylikData, setAylikData] = useState([]);
  const [vadesiGecenler, setVadesiGecenler] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ozetRes, aylikRes, vadesiGecenRes] = await Promise.all([
        axios.get(`${API}/raporlar/ozet`, { withCredentials: true }),
        axios.get(`${API}/raporlar/aylik`, { withCredentials: true }),
        axios.get(`${API}/cari-hesaplar/vadesi-gecenler`, { withCredentials: true })
      ]);
      setOzet(ozetRes.data);
      setAylikData(aylikRes.data);
      setVadesiGecenler(vadesiGecenRes.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-8 flex items-center justify-center min-h-screen">
          <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  const metrics = [
    {
      label: "Cari Bakiye",
      value: ozet?.cari_bakiye || 0,
      icon: Wallet,
      color: ozet?.cari_bakiye >= 0 ? "text-[#00C566]" : "text-[#FF3B30]"
    },
    {
      label: "Toplam Satış",
      value: ozet?.toplam_satis || 0,
      icon: ShoppingCart,
      color: "text-[#002FA7]"
    },
    {
      label: "Toplam Tahsilat",
      value: ozet?.toplam_tahsilat || 0,
      icon: ArrowUpRight,
      color: "text-[#00C566]"
    },
    {
      label: "Vadesi Geçen",
      value: ozet?.toplam_vadesi_gecen || 0,
      icon: AlertTriangle,
      color: "text-[#FF3B30]",
      count: ozet?.vadesi_gecen_adet || 0,
      isAlert: true
    }
  ];

  return (
    <Layout>
      <div className="p-6 lg:p-8" data-testid="dashboard-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-2xl font-bold text-[#0A0A0B] tracking-tight">
            Özet Panel
          </h1>
          <p className="text-[#5E5E62] text-sm mt-1">
            Şirketinizin finansal durumuna genel bakış
          </p>
        </div>

        {/* Metrics Grid */}
        <div className="dashboard-grid mb-8 rounded-sm overflow-hidden border border-[#E4E4E7]" data-testid="metrics-grid">
          {metrics.map((metric, index) => (
            <div
              key={metric.label}
              className={`metric-card animate-fade-in-up stagger-${index + 1} ${metric.isAlert && metric.value > 0 ? 'bg-[#FFEBEE]' : ''}`}
              data-testid={`metric-${metric.label.toLowerCase().replace(/ /g, '-')}`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="metric-label">{metric.label}</span>
                <metric.icon size={20} className={metric.color} />
              </div>
              <p className={`metric-value ${metric.color}`}>
                {formatCurrency(metric.value)}
              </p>
              {metric.count !== undefined && metric.count > 0 && (
                <p className="text-xs text-[#FF3B30] mt-1 font-medium">
                  {metric.count} adet bekliyor
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Second Row - Kar/Zarar + Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Kar/Zarar Summary */}
          <div className="bg-white border border-[#E4E4E7] rounded-sm p-6" data-testid="kar-zarar-card">
            <h3 className="font-heading font-semibold text-[#0A0A0B] mb-6">Kar / Zarar</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between py-3 border-b border-[#E4E4E7]">
                <div className="flex items-center gap-2">
                  <TrendingUp size={18} className="text-[#00C566]" />
                  <span className="text-sm text-[#5E5E62]">Toplam Gelir</span>
                </div>
                <span className="font-mono font-medium text-[#00C566]">
                  {formatCurrency(ozet?.toplam_gelir)}
                </span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-[#E4E4E7]">
                <div className="flex items-center gap-2">
                  <TrendingDown size={18} className="text-[#FF3B30]" />
                  <span className="text-sm text-[#5E5E62]">Toplam Gider</span>
                </div>
                <span className="font-mono font-medium text-[#FF3B30]">
                  {formatCurrency(ozet?.toplam_gider)}
                </span>
              </div>
              <div className="flex items-center justify-between py-3">
                <span className="text-sm font-medium text-[#0A0A0B]">Net Kar/Zarar</span>
                <span className={`font-mono font-bold text-lg ${ozet?.kar_zarar >= 0 ? 'text-[#00C566]' : 'text-[#FF3B30]'}`}>
                  {formatCurrency(ozet?.kar_zarar)}
                </span>
              </div>
            </div>
          </div>

          {/* Monthly Chart */}
          <div className="lg:col-span-2 bg-white border border-[#E4E4E7] rounded-sm p-6" data-testid="monthly-chart">
            <h3 className="font-heading font-semibold text-[#0A0A0B] mb-6">Aylık Performans</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={aylikData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                  <XAxis 
                    dataKey="ay" 
                    tick={{ fontSize: 11, fill: '#5E5E62' }}
                    axisLine={{ stroke: '#E4E4E7' }}
                    tickLine={false}
                  />
                  <YAxis 
                    tick={{ fontSize: 11, fill: '#5E5E62' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                  />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{
                      backgroundColor: '#0A0A0B',
                      border: 'none',
                      borderRadius: '2px',
                      color: '#fff',
                      fontSize: '12px'
                    }}
                  />
                  <Bar dataKey="gelir" fill="#00C566" name="Gelir" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="gider" fill="#FF3B30" name="Gider" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Overdue Accounts Alert */}
        {vadesiGecenler.length > 0 && (
          <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-hidden" data-testid="overdue-section">
            <div className="px-6 py-4 border-b border-[#E4E4E7] bg-[#FFEBEE]">
              <div className="flex items-center gap-2">
                <AlertTriangle size={20} className="text-[#FF3B30]" />
                <h3 className="font-heading font-semibold text-[#FF3B30]">
                  Vadesi Geçen Alacaklar ({vadesiGecenler.length})
                </h3>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E4E4E7]">
                    <th className="table-header">Müşteri</th>
                    <th className="table-header">Tutar</th>
                    <th className="table-header">Vade Tarihi</th>
                    <th className="table-header">Gecikme</th>
                  </tr>
                </thead>
                <tbody>
                  {vadesiGecenler.slice(0, 5).map((item) => {
                    const vadeTarihi = new Date(item.vade_tarihi);
                    const bugun = new Date();
                    const gecikmeGun = Math.floor((bugun - vadeTarihi) / (1000 * 60 * 60 * 24));
                    
                    return (
                      <tr key={item.id} className="table-row">
                        <td className="table-cell font-medium">{item.musteri_adi}</td>
                        <td className="table-cell font-mono text-[#FF3B30]">
                          {formatCurrency(item.tutar)}
                        </td>
                        <td className="table-cell font-mono text-[#5E5E62]">
                          {vadeTarihi.toLocaleDateString('tr-TR')}
                        </td>
                        <td className="table-cell">
                          <span className="badge-overdue">
                            <Clock size={12} className="mr-1" />
                            {gecikmeGun} gün
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;
